import React from "react";
import { motion } from "framer-motion";
import { GitCommit, Building2, MapPin } from "lucide-react";
import SectionHeading from "./SectionHeading";
import { experience } from "../data/mock";

const pseudoCommitHash = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(7, "0").slice(0, 7);
};

const Experience = () => {
  return (
    <section id="experience" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow="Experience"
          title="Commit log / career branch."
          description="Virtual internships building production-style backend services."
        />

        <div className="relative grid lg:grid-cols-12 gap-8">
          {/* Sticky side panel */}
          <div className="hidden lg:block lg:col-span-3 sticky top-32 self-start">
            <div className="p-5 rounded-2xl glass-strong">
              <div className="flex items-center gap-2 mb-3">
                <GitCommit className="w-4 h-4 text-cyan-300" />
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                  branch
                </p>
              </div>
              <p className="font-mono text-sm text-cyan-200">career/main</p>
              <div className="my-4 h-px bg-white/5" />
              <p className="font-display text-2xl font-semibold text-white">2025</p>
              <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
                Forage virtual internships — hands-on with Spring Boot, Kafka &amp; cloud architecture.
              </p>
              <div className="mt-4 flex items-center gap-3 text-[10px] font-mono text-zinc-500">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  merged
                </span>
                <span>·</span>
                <span>{experience.length} commits</span>
              </div>
            </div>
          </div>

          {/* Commit log */}
          <div className="lg:col-span-9 relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-cyan-400/60 via-white/10 to-transparent" />
            <div className="space-y-6">
              {experience.map((e, i) => {
                const commitHash = pseudoCommitHash(`${e.company}-${e.period}-${e.commit}`);
                return (
                <motion.div
                  key={e.company}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="relative pl-12"
                >
                  <div className="absolute left-0 top-3 w-9 h-9 rounded-full glass-strong flex items-center justify-center glow-cyan">
                    <GitCommit className="w-4 h-4 text-cyan-300" />
                  </div>

                  <div className="rounded-2xl glass overflow-hidden hover:border-white/15 transition-colors">
                    {/* Commit header */}
                    <div className="flex items-center flex-wrap gap-3 px-5 py-3 border-b border-white/5 bg-[#0a0b10]">
                      <span className="font-mono text-[10px] text-zinc-500 tracking-wider">
                        commit {commitHash}
                      </span>
                      <span className="font-mono text-[10px] text-cyan-300">
                        • vedant-bora
                      </span>
                      <span className="ml-auto font-mono text-[10px] text-zinc-500">
                        {e.period}
                      </span>
                    </div>

                    <div className="p-5 md:p-6">
                      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
                        <h3 className="font-display text-xl font-semibold text-white">
                          {e.company}
                        </h3>
                      </div>
                      <p className="text-sm text-zinc-300">{e.role}</p>
                      <p className="text-xs text-zinc-500 mt-1 inline-flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" />
                        {e.type}
                      </p>

                      {/* Commit message */}
                      <div className="mt-4 p-3 rounded-lg bg-black/30 border border-white/5">
                        <p className="font-mono text-[11px] text-cyan-300">
                          <span className="text-zinc-600">$</span> git log --oneline
                        </p>
                        <p className="font-mono text-[12px] text-zinc-300 mt-1">
                          <span className="text-amber-300">{commitHash}</span>{" "}
                          {e.commit}
                        </p>
                      </div>

                      <ul className="mt-4 space-y-2">
                        {e.points.map((pt, idx) => (
                          <li
                            key={idx}
                            className="flex gap-2.5 text-sm text-zinc-400 leading-relaxed"
                          >
                            <Building2 className="w-3.5 h-3.5 text-cyan-400/80 mt-0.5 shrink-0" />
                            {pt}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )})}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
